// API client + response types — one file because they evolve together and
// the surface is small (3 endpoints).

export interface Product {
  id: string;
  name: string;
  price_cents: number;
  sku: string;
  description: string;
  image_url: string;
  /** configurator_preloved_only — show this item only alongside a pre-loved
   *  machine; hidden from the normal new-machine flow. */
  preloved_only?: boolean;
}

export interface PreviewLineItem {
  product_id: string;
  name: string;
  unit_price_cents: number;
  quantity: number;
  line_total_cents: number;
}

export interface PreviewResponse {
  line_items: PreviewLineItem[];
  subtotal_cents: number;
  /** Auto-apply promo discount reflected in total_cents (0 when none matches). */
  discount_cents?: number;
  discount_label?: string;
  promo_code?: string;
  /** Retrofit trade-in credits, reflected in total_cents (separate from the
   *  promo discount). Absent from an older backend. */
  trade_ins?: { label: string; cents: number }[];
  trade_in_cents?: number;
  tax_cents: number;
  total_cents: number;
  tax_rate: number;
  /** When set (international destinations), used in place of the
   *  default "Sales Tax (X%)" row label — e.g. "Local Taxes to be Added". */
  tax_label?: string | null;
  /** Inferred from the ZIP/postal-code shape: "US" | "CA" | "OTHER". */
  country?: string | null;
}

export interface GenerateResponse {
  hubspot_quote_id: string;
  quote_public_url: string;
  quote_pdf_url: string;
  quote_number: string;
  hubspot_deal_id: string;
  hubspot_contact_id: string;
  shareable_id: string;
  subtotal_cents: number;
  /** All discounts applied (promo, employee, retrofit trade-in credits). */
  discount_cents?: number;
  tax_cents: number;
  total_cents: number;
}

/** Employee mode — superpowers a Gammill rep applies to a quote. Sent on
 *  generate; the backend ignores it unless `token` validates. */
export type DiscountType = "percent" | "fixed" | "tiered";

export interface EmployeeQuoteFields {
  token: string;
  /** Promo code: auto-applies a template + discount server-side. */
  promo_id?: string;
  /** Explicit HubSpot quote template id (overrides the promo's template). */
  template_id?: string;
  /** The rep's manual discount — always a GLOBAL line (e.g. a trade-in),
   *  additive to any promo discount. value = percent when type is "percent",
   *  dollars when type is "fixed". */
  discount?: { type: DiscountType; value: number; label?: string };
}

/** A promo's discount base: the whole order, or the machine/retrofit line only. */
export type DiscountScope = "order" | "machine";

export interface EmployeeRep {
  name: string;
  hubspot_owner_id: string;
  /** Admins can manage promo codes from inside employee mode. */
  is_admin?: boolean;
}

/** Full promo record for the admin management UI. */
export interface PromoFull {
  id: number;
  code: string;
  label: string;
  template_id: string;
  preloved_template_id: string;
  preloved_dated_template_id: string;
  preloved_graded_template_id: string;
  discount_type: "" | DiscountType;
  discount_value: number;
  /** Tiered only: "up_to_price:dollars_off" per line; "*" = and above. */
  discount_tiers: string;
  discount_scope: DiscountScope;
  active: boolean;
  /** Auto-apply config. When auto_apply, the promo matches carts by
   *  kind/family/product within the active window (Central dates). */
  auto_apply: boolean;
  starts_on: string; // YYYY-MM-DD (Central), "" = always
  ends_on: string; // YYYY-MM-DD (Central), "" = never
  trigger_kinds: string[]; // machine | retrofit | preloved | upgrade
  trigger_families: string[]; // ascend | statler | vision
  trigger_product_ids: string; // comma-separated
  nudge_message: string;
  applied_message: string;
}

/** Editable promo fields sent to the admin save endpoint. */
export interface PromoSave {
  id?: number;
  code: string;
  label: string;
  template_id: string;
  preloved_template_id: string;
  preloved_dated_template_id: string;
  preloved_graded_template_id: string;
  discount_type: "" | DiscountType;
  discount_value: number;
  discount_tiers: string;
  discount_scope: DiscountScope;
  active: boolean;
  auto_apply: boolean;
  starts_on: string;
  ends_on: string;
  trigger_kinds: string[];
  trigger_families: string[];
  trigger_product_ids: string;
  nudge_message: string;
  applied_message: string;
}

/** Response to POST /employee/auth/ — exactly one branch is populated:
 *  a personal PIN returns {token, rep}; the enrollment PIN returns
 *  {enroll, reps}. */
export interface EmployeeAuthResponse {
  token?: string;
  rep?: EmployeeRep;
  enroll?: boolean;
  reps?: { id: number; name: string }[];
}

export interface QuoteTemplate {
  id: string;
  name: string;
  active: boolean;
}

export interface EmployeePromo {
  code: string;
  label: string;
}

export interface QuoteRequestLineItem {
  product_id: string;
  quantity: number;
}

/** Existing-system form answers (e.g. {wheels: "metal", controller: "mod_g"}).
 *  The backend derives Statler/Ascend Retrofit trade-in credits from them. */
export interface ExistingSystemFields {
  existing_system?: Record<string, string>;
}

/** Pre-loved deep-link (gammill.com/preloved "Build Machine"): routes the quote
 *  to the Pre-Loved template and overrides the main line item's name + price
 *  with a specific used unit. Sent on preview, lead capture, and generate. */
export interface PrelovedFields {
  template?: "preloved";
  /** `family` + `category` let the backend apply the Statler-only
   *  Ascend-upgrade price override ($7,000 Pre-Loved, $6,000 Show / Classroom /
   *  Paint Flaw) when re-pricing the quote server-side. */
  preloved?: {
    description: string;
    price_cents: number;
    family?: string;
    /** Deal-name category: "Pre-Loved" | "Paint Flaw" | "Show" | "Classroom". */
    category?: string;
    /** Class A/B/C for a graded unit; drives the graded-vs-dated promo split. */
    klass?: string;
  };
}

export interface PreviewRequest extends PrelovedFields, ExistingSystemFields {
  line_items: QuoteRequestLineItem[];
  zip: string;
  state: string;
  /** Hidden test trigger: forces the named promo active regardless of dates. */
  promo_preview?: string;
  /** Employee mode: a rep has picked a promo, so don't preview the auto-apply
   *  one (their explicit pick governs the quote). */
  employee_promo?: boolean;
}

/** An active auto-apply promo, for the builder's banner/nudge. */
export interface ActivePromo {
  code: string;
  kinds: string[];
  families: string[];
  product_ids: string[];
  discount_type: "" | DiscountType;
  discount_value: number;
  /** Tiered only, parsed: first tier whose up_to_cents (null = and above)
   *  covers the base price wins. */
  discount_tiers: { up_to_cents: number | null; off_cents: number }[];
  discount_scope: DiscountScope;
  nudge_message: string;
  applied_message: string;
  /** ISO end of the active window (null = no end). Drives the countdown. */
  ends_at: string | null;
}

/** Structured pieces the backend composes into the HubSpot Deal Name:
 *  `Customer - Category - Machine Type - Size - Table` (empty parts dropped,
 *  hyphen-delimited). The frontend derives these from the selection /
 *  pre-loved deep-link; the backend owns the final string format. */
export interface DealMeta {
  /** "New" for normal builds; pre-loved channel sends the unit's category
   *  ("Pre-Loved" | "Paint Flaw" | "Show" | "Classroom"). */
  category: string;
  /** 4-digit year, when the unit is dated (mutually exclusive with klass). */
  year: string | null;
  /** Grade A/B/C, when the unit is graded instead of dated. */
  klass: string | null;
  /** "Statler" | "Vision" | "Ascend" (Ascend covers the Statler+Ascend
   *  toggle and the Ascend retrofit/upgrade — never "with Ascend upgrade"). */
  machine_type: string | null;
  /** Throat size in inches; backend renders as e.g. 26" (never the word
   *  "throat"). Null when unknown (omitted from the name). */
  machine_size_inches: number | null;
  /** e.g. "10' GS6" (never the word "table"). Null when there's no table. */
  table_size: string | null;
}

export interface GenerateRequest extends PrelovedFields, ExistingSystemFields {
  line_items: QuoteRequestLineItem[];
  /** Drives the composed HubSpot Deal Name. */
  deal_meta?: DealMeta;
  customer: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  /** Customer's chosen financing assumptions (from Edit Financing).
   *  Backend uses these to compute the monthly-payment block on the
   *  HubSpot quote so the customer sees the same number on the quote
   *  as they did on the configurator. */
  financing?: {
    credit_tier: string;
    term_months: number;
    down_payment_cents: number;
  };
  /** Hidden test trigger: forces the named promo active regardless of dates. */
  promo_preview?: string;
  /** Present only when an authenticated rep built the quote (employee mode). */
  employee?: EmployeeQuoteFields;
  /** Whether to email the customer their quote (via the HubSpot workflow).
   *  Defaults true server-side; the employee "Create Deal & Quote" sends false. */
  send_email?: boolean;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

// Where the Django backend lives. In dev this is "/api" — Vite's proxy
// forwards to localhost:8000.
// Root-relative so the API is always same-origin with the page:
//  - embedded on gammill.com/build/  → gammill.com/api/*  (apex Cloudflare
//    Worker `configurator-api-proxy` proxies these to the droplet, so the
//    browser never makes a cross-site request — required for the Facebook
//    in-app browser, which blocks third-party domains)
//  - standalone on buildamachine.gammilldealer.com → same-origin already
// Overridable at build time via VITE_API_BASE (see deploy/deploy.sh).
const API_BASE: string = import.meta.env.VITE_API_BASE || "/api";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  let body: unknown = null;
  try {
    body = await resp.json();
  } catch {
    // empty/non-JSON body — fine
  }
  if (!resp.ok) {
    const message =
      (body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `${resp.status} ${resp.statusText}`);
    throw new ApiError(resp.status, message);
  }
  return body as T;
}

export const api = {
  listProducts: () =>
    request<{ products: Product[] }>("/products/").then((r) => r.products),

  previewQuote: (body: PreviewRequest) =>
    request<PreviewResponse>("/quote/preview/", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  generateQuote: (body: GenerateRequest) =>
    request<GenerateResponse>("/quote/generate/", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Upsert a HubSpot Contact from the customer's details — no Deal, no
   *  Quote. Fires on the Calculate-button click so sales has a captured
   *  lead even if the customer bails after seeing the totals. The
   *  line_items get rendered into a Note attached to the contact so
   *  sales sees what configuration the shopper was looking at. */
  captureLead: (body: PrelovedFields & ExistingSystemFields & {
    customer: GenerateRequest["customer"];
    line_items: QuoteRequestLineItem[];
  }) =>
    request<{
      hubspot_contact_id: string;
      hubspot_note_id: string | null;
    }>("/lead/capture/", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Customer asked to be prequalified for financing. Upserts the
   *  contact + attaches a "💳 Configurator prequalification request"
   *  note describing their assumed credit tier, term, and down payment.
   *  Workflow lever for the financing team. */
  prequalify: (body: {
    customer: GenerateRequest["customer"];
    credit_tier: string;
    term_months: number;
    down_payment_cents: number;
    line_items?: QuoteRequestLineItem[];
  }) =>
    request<{
      hubspot_contact_id: string;
      hubspot_note_id: string | null;
    }>("/financing/prequalify/", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Public: active auto-apply promos for the builder banner/nudge. Pass the
   *  hidden `promoPreview` code to include an out-of-window promo for testing. */
  promosActive: (promoPreview?: string) =>
    request<{ promos: ActivePromo[] }>(
      `/promos/active/${promoPreview ? `?promo_preview=${encodeURIComponent(promoPreview)}` : ""}`,
    ).then((r) => r.promos),

  /** Employee mode step 1: exchange a code for either a session token (a
   *  rep's personal PIN) or an enrollment list (the global enrollment PIN). */
  employeeAuth: (code: string) =>
    request<EmployeeAuthResponse>("/employee/auth/", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  /** Employee mode: a not-yet-enrolled rep sets their personal PIN. */
  employeeEnroll: (body: { rep_id: number; pin: string; pin_confirm: string }) =>
    request<{ token: string; rep: EmployeeRep }>("/employee/enroll/", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Employee mode: active/non-hidden HubSpot quote templates for the picker. */
  employeeTemplates: (token: string) =>
    request<{ templates: QuoteTemplate[] }>(
      `/employee/templates/?token=${encodeURIComponent(token)}`,
    ).then((r) => r.templates),

  /** Employee mode: active promo codes for the picker. */
  employeePromos: (token: string) =>
    request<{ promos: EmployeePromo[] }>(
      `/employee/promos/?token=${encodeURIComponent(token)}`,
    ).then((r) => r.promos),

  /** Admin: list all promos (full fields) for management. */
  employeePromosManage: (token: string) =>
    request<{ promos: PromoFull[] }>(
      `/employee/promos/manage/?token=${encodeURIComponent(token)}`,
    ).then((r) => r.promos),

  /** Admin: create or update a promo. Omit `id` to create. */
  employeePromoSave: (token: string, promo: PromoSave) =>
    request<PromoFull>("/employee/promos/manage/", {
      method: "POST",
      body: JSON.stringify({ token, ...promo }),
    }),

  /** Employee mode: prefill the form from an existing HubSpot contact by email
   *  (email is unique in HubSpot, so at most one match). */
  employeeContactLookup: (token: string, email: string) =>
    request<{
      found: boolean;
      contact: null | {
        first_name: string;
        last_name: string;
        phone: string;
        address: string;
        city: string;
        state: string;
        zip: string;
      };
    }>(
      `/employee/contact-lookup/?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`,
    ),

  /** Admin: delete a promo. */
  employeePromoDelete: (token: string, id: number) =>
    request<{ ok: boolean }>("/employee/promos/delete/", {
      method: "POST",
      body: JSON.stringify({ token, id }),
    }),

  /** Customer asked for trade-in valuation on their existing machine.
   *  Upserts the contact + attaches a "🔄 Configurator trade-in
   *  valuation request" note with brand/model/year/serial/hours/
   *  condition/notes. Serial number is the load-bearing field per
   *  Gammill's trade-in process. */
  submitTradeIn: (body: {
    customer: GenerateRequest["customer"];
    brand: string;
    model: string;
    year: string;
    serial: string;
    hours: string;
    condition: string;
    notes: string;
  }) =>
    request<{
      hubspot_contact_id: string;
      hubspot_note_id: string | null;
    }>("/tradein/submit/", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
